import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { characters, getCharacter } from "@/mocks/characters";
import { getWork } from "@/mocks/works";
import CharacterCard from "@/components/feature/CharacterCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

export default function CharacterDetail() {
  const { id } = useParams<{ id: string }>();
  const c = id ? getCharacter(id) : undefined;
  const work = c ? getWork(c.workId) : undefined;

  const related = useMemo(
    () => (c ? characters.filter((x) => x.workId === c.workId && x.id !== c.id) : []),
    [c]
  );

  usePageMeta(
    c ? `${c.name}｜${c.epithet} - 女神漫画角色图鉴` : "角色未找到 - 女神漫画角色图鉴",
    c ? `${c.name}（${c.epithet}）来自《${work?.title}》，身份为${c.race}，人气值 ${c.rarity}。查看她的详细背景、性格特点与名场面介绍。` : "没有找到该女神角色。"
  );

  useJsonLd(
    c
      ? {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: `${c.name}（${c.epithet}）角色介绍`,
          description: c.description,
          image: c.image,
          author: { "@type": "Organization", name: "女神漫画角色图鉴" },
        }
      : null,
    `character-${id}`
  );

  if (!c) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center px-4 pt-16 text-center">
        <i className="ri-moon-foggy-line text-5xl text-background-300" aria-hidden="true"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold">未找到这位女神角色</h1>
        <p className="mt-2 text-sm text-foreground-600">可能链接已失效，返回图鉴继续探索吧。</p>
        <Link
          to="/characters"
          className="mt-6 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap hover:bg-primary-600"
        >
          返回角色图鉴
        </Link>
      </div>
    );
  }

  return (
    <>
      <section className="relative flex min-h-[320px] md:min-h-[380px] w-full items-end overflow-hidden bg-foreground-950">
        <img
          src={c.image}
          alt={`${c.name} 角色主视觉`}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/30"></div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-8 pt-20 md:px-6 md:pt-28">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-white/70">
            <Link to="/" className="hover:text-white" rel="nofollow">首页</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <Link to="/characters" className="hover:text-white">角色图鉴</Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-white/90">{c.name}</span>
          </nav>
          <div className="mt-5 flex flex-wrap items-end justify-between gap-4 text-white">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-accent-500 px-3 py-1 text-xs text-background-50">{work?.title}</span>
                <span className="rounded-full border border-white/30 px-3 py-1 text-xs">{c.race}</span>
              </div>
              <h1 className="mt-3 font-heading text-3xl font-black md:text-5xl">{c.name}</h1>
              <p className="mt-2 text-sm text-white/80 md:text-base">{c.epithet} · {c.nameEn}</p>
            </div>
            <div className="flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 backdrop-blur">
              <i className="ri-heart-3-fill text-accent-400" aria-hidden="true"></i>
              <span className="text-2xl font-bold text-accent-300">{c.rarity}</span>
              <span className="text-xs text-white/70">人气值</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <div className="overflow-hidden rounded-lg border border-background-200">
                  <div className="aspect-[3/4] overflow-hidden">
                    <img src={c.image} alt={`${c.name} 女神角色立绘`} className="h-full w-full object-cover object-top" />
                  </div>
                </div>
                <h2 className="mt-6 font-heading text-lg font-bold">设定标签</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  {c.tags.map((t) => (
                    <span key={t} className="rounded-full bg-accent-100 px-3 py-1 text-xs text-accent-800 whitespace-nowrap">
                      #{t}
                    </span>
                  ))}
                </div>
                <h2 className="mt-6 font-heading text-lg font-bold">性格特点</h2>
                <ul className="mt-3 grid grid-cols-2 gap-2">
                  {c.personality.map((p) => (
                    <li key={p} className="flex items-center gap-2 rounded-lg border border-background-200 bg-background-100 px-3 py-2 text-sm text-foreground-700">
                      <i className="ri-verified-badge-line text-accent-500" aria-hidden="true"></i>
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="lg:col-span-8">
              <h2 className="font-heading text-2xl font-bold md:text-3xl">角色介绍</h2>
              <p className="mt-4 text-sm leading-loose text-foreground-700 md:text-base">
                {c.description}
              </p>

              <h2 className="mt-10 font-heading text-2xl font-bold md:text-3xl">名场面</h2>
              <div className="mt-5 space-y-4">
                {c.classicScene.map((s, i) => (
                  <article key={s.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-500 text-background-50">
                        <i className="ri-star-fill" aria-hidden="true"></i>
                      </span>
                      <h3 className="font-heading text-base font-bold">
                        {i + 1}. {s.title}
                      </h3>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
                  </article>
                ))}
              </div>

              {related.length > 0 && (
                <>
                  <h2 className="mt-10 font-heading text-2xl font-bold md:text-3xl">同作品女神推荐</h2>
                  <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-3">
                    {related.map((r) => (
                      <CharacterCard key={r.id} c={r} />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}